import * as logClient from "@classdojo/log-client";
import { useEffect, useRef, useState, useCallback } from "react";
import { useInterval } from "@web-monorepo/hooks";

export const playStates = {
  READY: "ready",
  PLAYING: "playing",
  PAUSED: "paused",
  DONE: "done",
} as const;

type PlayStateKeys = keyof typeof playStates;
type PlayStateValues = (typeof playStates)[PlayStateKeys];

type UseAudioPlayerArgs = {
  src: string;
  expectedDuration: number;
};

export function useAudioPlayer({ src, expectedDuration }: UseAudioPlayerArgs) {
  const audioNodeRef = useRef<HTMLAudioElement>();
  const playStateRef = useRef<PlayStateValues>();
  const [playState, setPlayState] = useState<PlayStateValues>(playStates.READY); // TODO: technically not ready until buffered
  const [secondsElapsed, setSecondsElapsed] = useState(0);

  playStateRef.current = playState;

  const play = useCallback(() => {
    audioNodeRef.current?.play().catch(() => {
      logClient.sendMetrics([
        {
          type: "increment",
          value: 1,
          metricName: "toolkit.media.audio.play.error",
        },
      ]);
    });
  }, [audioNodeRef]);
  const pause = useCallback(() => audioNodeRef.current?.pause(), [audioNodeRef]);

  // when playing, update every second
  useInterval(
    () => {
      if (audioNodeRef.current) {
        setSecondsElapsed(audioNodeRef.current.currentTime);
      }
    },
    playState === playStates.PLAYING ? 1000 : null,
  );

  // replace audio DOM node when src changes

  useEffect(() => {
    const audioNode = document.createElement("audio");
    audioNode.autoplay = false;
    audioNode.src = src;
    audioNode.onended = () =>
      // bulk ignoring existing errors
      // eslint-disable-next-line @web-monorepo/no-setState-in-useEffect
      setPlayState(playStates.DONE);
    audioNode.onpause = () =>
      // bulk ignoring existing errors
      // eslint-disable-next-line @web-monorepo/no-setState-in-useEffect
      setPlayState(playStates.PAUSED);
    audioNode.onplay = () =>
      // bulk ignoring existing errors
      // eslint-disable-next-line @web-monorepo/no-setState-in-useEffect
      setPlayState(playStates.PLAYING);
    document.body.appendChild(audioNode);

    audioNodeRef.current = audioNode;

    return () => {
      audioNodeRef.current?.remove();
      audioNodeRef.current = undefined;
    };
  }, [src]);

  // pause if screen is hidden

  useEffect(() => {
    const onVisibilityChange = () => {
      if (
        audioNodeRef.current &&
        document.hidden &&
        // NOTE: need to use ref here since visibilitychange callback is defined once on first render
        playStateRef.current == playStates.PLAYING
      ) {
        audioNodeRef.current.pause();
      }
    };

    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  });

  return {
    play,
    pause,
    playState,
    secondsRemaining: Math.max(0, Math.floor(expectedDuration - secondsElapsed)),
  };
}
